import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';

import { useMultiStyleConfig } from '../../system/hooks';
import { Flex } from '../../theme';
import { InferPropTypes } from '../../types';

import getChildAvatars from './getChildAvatars';

import DisplayNames from 'atoms/Avatar/enums';
/*
  ! Why did we make a custom AvatarGroup instead of extending Chakras?
  * AvatarTheme: Some things with are borked: (Sept 7th, 2021):
      1. backgroundColor is showing through avatars (https://github.com/chakra-ui/chakra-ui/issues/4304)
      2. borderRadius is not respected when passing in images (goes back to a circle, which is chakra default)
  * AvatarTheme (is largely not implemented in a way which fits our spec):
    * 1. To achieve the "stagger" effect design needs we had to do some custom theme work as well.
    * 2. MoreIndicator will always manually set a "+" which doesnt match our mocks.
    * 3. The ChakraAvatarGroup returns children in a reversed list which is harder to work with.
*/

export type AvatarGroupSizes = 'sm' | 'md' | 'lg';
export const sizePropTypes: AvatarGroupSizes[] = ['sm', 'md', 'lg'];

const propTypes = {
  staggered: PropTypes.bool,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(sizePropTypes),
  __css: PropTypes.shape({})
};
const defaultProps = {
  staggered: false,
  size: 'md',
  __css: null
};

export type AvatarGroupProps = InferPropTypes<typeof propTypes, typeof defaultProps>;

export default function AvatarGroup(props: AvatarGroupProps): React.ReactElement {
  const { children, size, staggered, __css } = props;
  const avatars = React.Children.toArray(children);

  const { avatarList } = useMultiStyleConfig('AvatarCustomTheme', { size, staggered, avatars });

  return (
    <Flex __css={mergeWith(avatarList, __css)} role="group">
      {getChildAvatars(avatars, props)}
    </Flex>
  );
}

AvatarGroup.displayName = DisplayNames.AvatarGroup;
AvatarGroup.propTypes = propTypes;
AvatarGroup.defaultProps = defaultProps;
